import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import { defineView } from "foldkit/submodel";

import { DELETE_CONFIRMATION } from "../../api/commands/account";
import { i18next } from "../../i18n";
import { confirmDialog } from "../../widgets/dialog";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";

/** v1 keys `settings.theme.*`. The label reflects the root theme (light → offer dark). */
export const view = defineView<Model, Message, Shared>((model, shared, h) => {
	const exporting = AsyncData.isLoading(model.usageExport);
	const exportingMarkdown = AsyncData.isLoading(model.markdownExport);
	const subscribed =
		AsyncData.getOrElse(model.digest, () => null)?.subscribed ?? null;
	const digestBusy =
		AsyncData.isLoading(model.digest) || AsyncData.isRefreshing(model.digest);
	const aiEnabled = AsyncData.getOrElse(model.ai, () => null)?.enabled ?? null;
	const aiBusy =
		AsyncData.isLoading(model.ai) || AsyncData.isRefreshing(model.ai);
	const themeLabel =
		shared.theme === "light"
			? i18next.t("settings.theme.switchToDark")
			: i18next.t("settings.theme.switchToLight");
	return h.div(
		[h.DataAttribute("testid", "page-Settings"), h.Class("max-w-4xl mx-auto")],
		[
			h.div(
				[
					h.Class(
						"space-y-4 rounded-2xl border border-base-300 bg-base-100 p-6 shadow-sm",
					),
				],
				[
					h.div(
						[],
						[
							h.strong([], [i18next.t("settings.theme.label")]),
							h.div(
								[h.Class("flex items-center gap-2")],
								[
									h.button(
										[
											h.Type("button"),
											h.Class("p-2 bg-base-200 rounded-sm"),
											h.AriaLabel(themeLabel),
											h.OnClick(Message.ClickedThemeToggle()),
										],
										[themeLabel],
									),
									h.button(
										[
											h.Type("button"),
											h.Class("p-2 bg-base-200 rounded-sm"),
											h.AriaLabel(i18next.t("settings.theme.useSystemDefault")),
											h.OnClick(Message.ClickedSystemTheme()),
										],
										[i18next.t("settings.theme.useSystemDefault")],
									),
								],
							),
						],
					),
					h.div(
						[h.Class("space-y-2")],
						[
							h.strong([], [i18next.t("settings.digest.title")]),
							h.p(
								[h.Class("text-sm text-base-content/70 max-w-xl")],
								[i18next.t("settings.digest.description")],
							),
							h.label(
								[h.Class("flex items-center gap-3")],
								[
									h.input([
										h.Type("checkbox"),
										h.Class("toggle toggle-primary"),
										h.Checked(subscribed === true),
										h.Disabled(digestBusy || subscribed === null),
										h.AriaLabel(i18next.t("settings.digest.toggle")),
										h.OnClick(
											Message.ToggledDigest({ subscribed: !subscribed }),
										),
									]),
									h.span(
										[h.Class("text-sm")],
										[
											AsyncData.isFailure(model.digest)
												? i18next.t("settings.digest.loadError")
												: subscribed
													? i18next.t("settings.digest.on")
													: i18next.t("settings.digest.off"),
										],
									),
								],
							),
						],
					),
					h.div(
						[h.Class("space-y-2")],
						[
							h.strong([], [i18next.t("settings.ai.title")]),
							h.p(
								[h.Class("text-sm text-base-content/70 max-w-xl")],
								[i18next.t("settings.ai.description")],
							),
							h.label(
								[h.Class("flex items-center gap-3")],
								[
									h.input([
										h.Type("checkbox"),
										h.Class("toggle toggle-primary"),
										h.Checked(aiEnabled === true),
										h.Disabled(aiBusy || aiEnabled === null),
										h.AriaLabel(i18next.t("settings.ai.toggle")),
										h.OnClick(Message.ToggledAi({ enabled: !aiEnabled })),
									]),
									h.span(
										[h.Class("text-sm")],
										[
											AsyncData.isFailure(model.ai)
												? i18next.t("settings.ai.loadError")
												: aiEnabled
													? i18next.t("settings.ai.on")
													: i18next.t("settings.ai.off"),
										],
									),
								],
							),
						],
					),
					h.div(
						[h.Class("space-y-2")],
						[
							h.strong([], [i18next.t("settings.dataExport.title")]),
							h.p(
								[h.Class("text-sm text-base-content/70 max-w-xl")],
								[i18next.t("settings.dataExport.description")],
							),
							h.div(
								[h.Class("flex items-center gap-3")],
								[
									h.button(
										[
											h.Type("button"),
											h.Class("btn btn-secondary btn-sm"),
											h.AriaLabel(i18next.t("settings.dataExport.button")),
											h.Disabled(exporting),
											h.OnClick(Message.ClickedExport()),
										],
										[
											exporting
												? i18next.t("settings.dataExport.exporting")
												: i18next.t("settings.dataExport.button"),
										],
									),
									h.button(
										[
											h.Type("button"),
											h.Class("btn btn-secondary btn-sm"),
											h.AriaLabel(
												i18next.t("settings.dataExport.markdownButton"),
											),
											h.Disabled(exportingMarkdown),
											h.OnClick(Message.ClickedExportMarkdown()),
										],
										[
											exportingMarkdown
												? i18next.t("settings.dataExport.exporting")
												: i18next.t("settings.dataExport.markdownButton"),
										],
									),
									Option.match(model.feedback, {
										onNone: () => h.empty,
										onSome: (feedback) =>
											h.span(
												[
													h.Role("status"),
													h.Class(
														feedback.kind === "success"
															? "text-success text-sm"
															: "text-error text-sm",
													),
												],
												[feedback.message],
											),
									}),
								],
							),
						],
					),
					h.div(
						[h.Class("space-y-2")],
						[
							h.strong([], [i18next.t("settings.deleteData.title")]),
							h.p(
								[h.Class("text-sm text-base-content/70 max-w-xl")],
								[i18next.t("settings.deleteData.description")],
							),
							Option.match(model.deleteError, {
								onNone: () => h.empty,
								onSome: (error) =>
									h.p(
										[h.Role("alert"), h.Class("text-error text-sm")],
										[error],
									),
							}),
							h.input([
								h.Type("text"),
								h.Class("input input-bordered input-sm block"),
								h.Value(model.deleteConfirmation),
								h.Placeholder(DELETE_CONFIRMATION),
								h.AriaLabel(
									i18next.t("settings.deleteData.typeToConfirm", {
										word: DELETE_CONFIRMATION,
									}),
								),
								h.OnInput((value) =>
									Message.ChangedDeleteConfirmation({ value }),
								),
							]),
							h.button(
								[
									h.Type("button"),
									h.Class("btn btn-error btn-sm"),
									h.AriaLabel(i18next.t("settings.deleteData.title")),
									h.Disabled(model.deleteConfirmation !== DELETE_CONFIRMATION),
									h.OnClick(Message.ClickedDelete()),
								],
								[i18next.t("settings.deleteData.title")],
							),
						],
					),
					confirmDialog(h, {
						slotId: "confirm-delete",
						model: model.dialog,
						toParentMessage: (message) => Message.GotDialogMessage({ message }),
						// v1 hardcodes these two strings (no translation key).
						title: "Confirm Deletion",
						cancelLabel: "Cancel",
						message: i18next.t("settings.deleteData.confirmMessage"),
						confirmLabel: i18next.t("settings.deleteData.title"),
						onConfirm: Message.ClickedConfirmDelete(),
						isPending: model.deleting,
					}),
				],
			),
		],
	);
});
